import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import type { EntityRow } from './types';
import { CHART_COLORS_EXT } from './constants';

interface EntityBarChartProps {
  data: EntityRow[];
  metric: 'passPercentage' | 'performanceIndex';
  title: string;
  limit?: number;
  onBarClick?: (name: string) => void;
}

const EntityBarChart: React.FC<EntityBarChartProps> = function ({ data, metric, title, limit, onBarClick }) {
  const lim = limit ?? 20;
  const displayed = [...data].sort(function (a, b) { return b[metric] - a[metric]; }).slice(0, lim);
  const avg = displayed.length
    ? +(displayed.reduce(function (s, d) { return s + d[metric]; }, 0) / displayed.length).toFixed(2)
    : 0;

  if (!displayed.length) {
    return (
      <div className="bg-white rounded-xl border p-6 text-center text-gray-400 text-sm h-40 flex items-center justify-center">
        No data
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-1 gap-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
          Avg: {avg}%
        </span>
      </div>
      {onBarClick && <p className="text-xs text-gray-400 mb-3">Click a bar to drill down</p>}
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: Math.max(400, displayed.length * 64) }}>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={displayed}
              margin={{ top: 20, right: 16, left: 8, bottom: 80 }}
              onClick={function (e: any) { if (e && e.activeLabel && onBarClick) onBarClick(e.activeLabel); }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                angle={-35}
                height={90}
                textAnchor="end"
                tick={function (props: any) {
                  const { x, y, payload } = props;
                  const words = String(payload.value).split(' ');
                  const mid = Math.ceil(words.length / 2);
                  const line1 = words.slice(0, mid).join(' ');
                  const line2 = words.slice(mid).join(' ');
                  return (
                    <g transform={`translate(${x},${y}) rotate(-35)`}>
                      <text x={0} y={0} textAnchor="end" fill="#667085" fontSize={10}>
                        <tspan x={0} dy={4}>{line1}</tspan>
                        {line2 && <tspan x={0} dy={13}>{line2}</tspan>}
                      </text>
                    </g>
                  );
                }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#667085' }}
                tickLine={false}
                axisLine={false}
                domain={[0, 100]}
                tickFormatter={function (v: number) { return v + '%'; }}
              />
              <ReferenceLine y={avg} stroke="#9ca3af" strokeDasharray="4 4" />
              <Tooltip
                content={function (props: any) {
                  const { active, payload, label } = props;
                  if (!active || !payload || !payload.length) return null;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
                      <p className="font-semibold text-gray-800 mb-1">{label}</p>
                      <p style={{ color: payload[0].fill }}>
                        {metric === 'passPercentage' ? 'Pass %' : 'PI'}:{' '}
                        <span className="font-bold">{payload[0].value}%</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey={metric}
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
              >
                {displayed.map(function (_, i) {
                  return <Cell key={i} fill={CHART_COLORS_EXT[i % CHART_COLORS_EXT.length]} fillOpacity={0.85} />;
                })}
                <LabelList
                  dataKey={metric}
                  position="top"
                  formatter={function (v: any) { return v + '%'; }}
                  style={{ fontSize: 9, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default EntityBarChart;
